import React, { useState } from 'react';
import { FormHelperText, IconButton, TextField } from '@mui/material';
import { Topic } from '@shared/domain/topic/topic.aggregate';
import {
  Coordinates,
  Subtopic
} from '@shared/domain/subtopic/subtopic.aggregate';

import { Control, Controller, ControllerRenderProps } from 'react-hook-form';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import { SubtopicModal } from 'subtopic/subtopicModal';
import { ClickableCanvas } from 'subtopic/clickableCanvas';
import { validations } from '@shared/infrastructure/ui/validations';
import InputLabel from '@mui/material/InputLabel';

export const SubtopicInfo = ({
  topic,
  control,
  disableName
}: {
  topic?: Topic;
  control: Control<Subtopic, any>;
  disableName?: boolean;
}) => {
  const [positionModal, setPositionModal] = useState<{
    isOpen: boolean;
    field?: ControllerRenderProps<Subtopic, 'position'>;
  }>({
    isOpen: false
  });

  return (
    <>
      {positionModal.isOpen && (
        <SubtopicModal
          isOpen={positionModal.isOpen}
          width='1000px'
          height='500px'
          onClose={() => {
            positionModal.field!.onBlur();
            setPositionModal({ isOpen: false });
          }}
        >
          <ClickableCanvas
            position={{
              x: positionModal.field!.value?.x / 4,
              y: positionModal.field!.value?.y / 4
            }}
            onClick={(position: Coordinates) => {
              positionModal.field!.onBlur();
              positionModal.field!.onChange({
                x: position.x * 4,
                y: position.y * 4
              });
            }}
            background={topic?.panoramicImageUrl}
          ></ClickableCanvas>
        </SubtopicModal>
      )}

      <Controller
        name='name'
        control={control}
        rules={{
          ...validations.required(),
          ...validations.pattern(
            /^[a-z]+(-[a-z]+)*$/,
            'This value must be composed of groups of lowercase letters, separated by hypens (-).'
          )
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ my: '0.3rem' }}
            label='Identifier'
            placeholder='example-url'
            disabled={disableName}
          />
        )}
      />
      <Controller
        name='nameLanguage'
        control={control}
        rules={validations.notWhiteSpace()}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ my: '0.3rem' }}
            label='Title'
          />
        )}
      />
      <Controller
        name='descriptionLanguage'
        control={control}
        rules={validations.notWhiteSpace()}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ my: '0.3rem' }}
            label='Description'
            multiline
            minRows={2}
          />
        )}
      />
      <Controller
        name='position'
        control={control}
        rules={validations.required(
          'Select a position by clicking on the icon above.'
        )}
        render={({ field, fieldState }) => (
          <>
            <InputLabel error={!!fieldState.error}>
              Edit Topic Position
              <IconButton
                size='large'
                color='inherit'
                aria-label='open tab button'
                ref={field.ref}
                onClick={() => {
                  field.onBlur();
                  setPositionModal({ isOpen: true, field });
                }}
              >
                <PictureInPictureAltIcon></PictureInPictureAltIcon>
              </IconButton>
            </InputLabel>
            <FormHelperText
              sx={{ marginRight: '14px', marginLeft: '14px' }}
              error={!!fieldState.error}
            >
              {fieldState.error?.message}
            </FormHelperText>
          </>
        )}
      />
    </>
  );
};
